import { Button } from "components/DesignSystem/Button/Button";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import Input from "components/DesignSystem/Input/Input";
import { DateField } from "components/DesignSystem/DateInput/DateField";
import { FormikForm } from "components/FormikForm/FormikForm";
import { useFormikContext } from "formik";
import { MultiValue, SingleValue } from "react-select";
import { Countries } from "types/Models/countries";
import { AddressPicker } from "v2/components/AddressPicker/AddressPicker";
import { getEntityTypeLabel } from "v2/utils/getEntityTypeLabel";
import { OnboardingEntityDraft } from "v2/components/onboarding/OnboardingDataContext";
import { AddressData } from "v2/store/onboardingEntityApi";
import { LLC_TAX_OPTIONS } from "../EntityTypeStep/entityOptions";
import { ENTITY_TYPE_IDS } from "v2/constants/entityTypes";
import { AddEntityMode } from "./AddAnotherEntityDropdown";
import { buildAddEntitySchema } from "./validations/addEntityFormSchema";
import {
  getCountryCodeById,
  getCountryOptions,
  getDefaultSuffix,
  getFormationOptions,
  getFormationTypeLabel,
  getPrefixesForType,
  getSelectedEntityTypeOption,
} from "./entityFieldOptions";
import { SuffixSelect } from "./SuffixSelect";
import { US } from "constants/countryCodes";
import { getGroupedCountryOptionsByUuid } from "utils/stateRequired";
import { DELAWARE_STATE_UUID } from "constants/entity";

type RelationOption = { value: string; label: string };

type AddEntityFormValues = {
  incorporationCountryId: string;
  formationTypeId: string;
  entityName: string;
  entitySuffix: string;
  taxedAs: string;
  relatedEntityId: string;
  cStateId: string;
  dateOfIncorporation: string;
  streetAddress: string;
  addressLine2: string;
  city: string;
  state: string;
  country: string;
  zipcode: string;
};

type AddEntityFormProps = {
  initial?: OnboardingEntityDraft | null;
  mode: AddEntityMode;
  relationOptions: RelationOption[];
  countries: Countries[] | undefined;
  showBusinessAddress?: boolean;
  showStateField?: boolean;
  requireStateField?: boolean;
  showDateField?: boolean;
  singleColumn?: boolean;
  hiddenTaxOptionKeys?: string[];
  onSave: (draft: OnboardingEntityDraft) => void | Promise<void>;
  onCancel: () => void;
};

const EMPTY_HIDDEN_TAX_KEYS: string[] = [];

const TAX_OPTIONS = LLC_TAX_OPTIONS.map((option) => ({
  value: option.key,
  label: option.name,
}));

const relationLabel = (mode: AddEntityMode) =>
  mode === "subsidiary" ? "Parent entity" : "Subsidiary entity";

const AddEntityFormFields = ({
  mode,
  relationOptions,
  countries,
  showBusinessAddress,
  showStateField,
  requireStateField,
  showDateField,
  singleColumn,
  hiddenTaxOptionKeys,
  onCancel,
}: {
  mode: AddEntityMode;
  relationOptions: RelationOption[];
  countries: Countries[] | undefined;
  showBusinessAddress: boolean;
  showStateField: boolean;
  requireStateField: boolean;
  showDateField: boolean;
  singleColumn: boolean;
  hiddenTaxOptionKeys: string[];
  onCancel: () => void;
}) => {
  const { values, setFieldValue, isSubmitting, submitForm } =
    useFormikContext<AddEntityFormValues>();

  const countryOptions = getCountryOptions(countries);
  const groupedCountryOptions = getGroupedCountryOptionsByUuid(countries);
  const formationOptions = getFormationOptions(
    countries,
    values.incorporationCountryId
  );
  const suffixOptions = getPrefixesForType(
    countries,
    values.formationTypeId
  ).map((prefix) => prefix.prefix);
  const isLlc = values.formationTypeId === ENTITY_TYPE_IDS.llc;
  const showRelation = mode !== "separate";
  const isUsIncorporation =
    getCountryCodeById(countries, values.incorporationCountryId) === US;

  const selectedCountry = countries?.find(
    (country) => country.uuid === values.incorporationCountryId
  );

  const stateOptions = [...(selectedCountry?.states ?? [])]
    .sort((a, b) => {
      if (a.uuid === DELAWARE_STATE_UUID) return -1;
      if (b.uuid === DELAWARE_STATE_UUID) return 1;
      return 0;
    })
    .map((state) => ({ value: state.uuid, label: state.name }));

  const showState = showStateField && isUsIncorporation;
  const showAddress = showBusinessAddress && isUsIncorporation;

  const rowClassName = singleColumn
    ? "t-flex t-flex-col t-gap-4"
    : "t-grid t-grid-cols-2 sm:t-grid-cols-1 t-gap-4";

  const countryValue =
    countryOptions.find(
      (option) => option.value === values.incorporationCountryId
    ) ?? null;
  const formationValue = getSelectedEntityTypeOption(
    formationOptions,
    values.formationTypeId,
    values.taxedAs
  );
  const taxOptions = TAX_OPTIONS.filter(
    (option) => !hiddenTaxOptionKeys.includes(option.value)
  );
  const taxValue = taxOptions.find((o) => o.value === values.taxedAs) ?? null;
  const relationValue =
    relationOptions.find((o) => o.value === values.relatedEntityId) ?? null;
  const stateValue =
    stateOptions.find((o) => o.value === values.cStateId) ?? null;

  const clearAddress = () => {
    setFieldValue("streetAddress", "");
    setFieldValue("addressLine2", "");
    setFieldValue("city", "");
    setFieldValue("state", "");
    setFieldValue("country", "");
    setFieldValue("zipcode", "");
  };

  const handleCountryChange = (
    option: SingleValue<OptionData> | MultiValue<OptionData>
  ) => {
    setFieldValue("formationTypeId", "");
    setFieldValue("entitySuffix", "");
    setFieldValue("taxedAs", "");
    setFieldValue("cStateId", "");

    const selectedId = option && "value" in option ? String(option.value) : "";
    if (getCountryCodeById(countries, selectedId) !== US) clearAddress();
  };

  const handleStateChange = (
    option: SingleValue<OptionData> | MultiValue<OptionData>
  ) =>
    setFieldValue(
      "cStateId",
      option && "value" in option ? String(option.value) : ""
    );

  const handleFormationChange = (
    option: SingleValue<OptionData> | MultiValue<OptionData>
  ) => {
    const selectedId = option && "value" in option ? String(option.value) : "";
    setFieldValue(
      "entitySuffix",
      getDefaultSuffix(getPrefixesForType(countries, selectedId))
    );
    if (selectedId !== ENTITY_TYPE_IDS.llc) setFieldValue("taxedAs", "");
  };

  const handleTaxChange = (
    option: SingleValue<OptionData> | MultiValue<OptionData>
  ) =>
    setFieldValue(
      "taxedAs",
      option && "value" in option ? String(option.value) : ""
    );

  const handleRelationChange = (
    option: SingleValue<OptionData> | MultiValue<OptionData>
  ) =>
    setFieldValue(
      "relatedEntityId",
      option && "value" in option ? String(option.value) : "",
      true
    );

  const handleSave = () => {
    void submitForm();
  };

  return (
    <div className="t-flex t-flex-col t-gap-4 t-rounded-lg t-p-4 t-shadow-light-30">
      <div className={rowClassName}>
        <Combobox
          withForm
          required
          name="incorporationCountryId"
          label="Country of incorporation"
          placeholder="Select country"
          isClearable={false}
          menuPortalTarget={document.body}
          shouldValidateOnChange={false}
          onChange={handleCountryChange}
          value={countryValue}
          options={groupedCountryOptions}
        />
        {showState && (
          <Combobox
            withForm
            required={requireStateField}
            name="cStateId"
            label="State of incorporation"
            placeholder="Select state"
            isClearable={false}
            menuPortalTarget={document.body}
            shouldValidateOnChange={false}
            onChange={handleStateChange}
            value={stateValue}
            options={stateOptions}
          />
        )}
        <Combobox
          withForm
          required
          name="formationTypeId"
          label="Entity type"
          placeholder="Select entity type"
          isClearable={false}
          isDisabled={!values.incorporationCountryId}
          menuPortalTarget={document.body}
          shouldValidateOnChange={false}
          onChange={handleFormationChange}
          value={formationValue}
          options={formationOptions}
        />
      </div>
      {isLlc && (
        <Combobox
          withForm
          required
          name="taxedAs"
          label="How is your LLC taxed?"
          placeholder="Select tax classification"
          isClearable={false}
          menuPortalTarget={document.body}
          shouldValidateOnChange={false}
          onChange={handleTaxChange}
          value={taxValue}
          options={taxOptions}
        />
      )}
      <div className={rowClassName}>
        <Input
          withForm
          required
          showErrorOnceTouched
          name="entityName"
          label="Entity name"
          placeholder="Acme Technologies"
          rightComponent={
            suffixOptions.length ? (
              <SuffixSelect options={suffixOptions} />
            ) : undefined
          }
        />
        {showRelation && (
          <Combobox
            withForm
            required
            name="relatedEntityId"
            label={relationLabel(mode)}
            placeholder="Select entity"
            isClearable={false}
            menuPortalTarget={document.body}
            shouldValidateOnChange={false}
            onChange={handleRelationChange}
            value={relationValue}
            options={relationOptions}
          />
        )}
      </div>
      {showAddress && <AddressPicker label="Business address" />}
      {showDateField && (
        <DateField
          name="dateOfIncorporation"
          label="Date of incorporation"
          required
          showErrorOnceTouched
          placeholder="MMM DD, YYYY"
        />
      )}
      <div className="t-flex t-gap-2">
        <Button
          type="button"
          size="small"
          customType="primary_outlined"
          isLoading={isSubmitting}
          disabled={isSubmitting}
          onClick={handleSave}
        >
          Save
        </Button>
        <Button
          type="button"
          size="small"
          customType="secondary"
          disabled={isSubmitting}
          onClick={onCancel}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

export const AddEntityForm = ({
  initial,
  mode,
  relationOptions,
  countries,
  showBusinessAddress = true,
  showStateField = false,
  requireStateField = false,
  showDateField = false,
  singleColumn = false,
  hiddenTaxOptionKeys = EMPTY_HIDDEN_TAX_KEYS,
  onSave,
  onCancel,
}: AddEntityFormProps) => {
  const address = initial?.addressData;

  const usCountryIds = (countries ?? [])
    .filter((country) => country.code_alpha_2 === US)
    .map((country) => country.uuid);

  const initialValues: AddEntityFormValues = {
    incorporationCountryId:
      countries?.find((country) =>
        country.formation_type.some(
          (type) => type.uuid === initial?.formationTypeId
        )
      )?.uuid ?? "",
    formationTypeId: initial?.formationTypeId ?? "",
    entityName: initial?.entityName ?? "",
    entitySuffix: initial?.entitySuffix ?? "",
    taxedAs: initial?.taxedAs ?? "",
    relatedEntityId: initial?.relatedEntityId ?? "",
    cStateId: initial?.cStateId ?? "",
    dateOfIncorporation: initial?.dateOfIncorporation ?? "",
    streetAddress: address?.street_address ?? "",
    addressLine2: address?.street_address_line_2 ?? "",
    city: address?.city ?? "",
    state: address?.state ?? "",
    country: address?.country ?? "",
    zipcode: address?.zipcode ?? "",
  };

  const handleSubmit = async (values: AddEntityFormValues) => {
    const isUsIncorporation = usCountryIds.includes(
      values.incorporationCountryId
    );
    const addressData: AddressData | null =
      showBusinessAddress && isUsIncorporation
        ? {
            street_address: values.streetAddress,
            street_address_line_2: values.addressLine2,
            city: values.city,
            state: values.state,
            zipcode: values.zipcode,
            country: values.country,
          }
        : null;
    await onSave({
      localId: initial?.localId ?? crypto.randomUUID(),
      entityId: initial?.entityId,
      entityName: values.entityName.trim(),
      entitySuffix: values.entitySuffix,
      formationTypeId: values.formationTypeId,
      entityTypeLabel: getEntityTypeLabel(
        getFormationTypeLabel(countries, values.formationTypeId),
        values.taxedAs,
        values.formationTypeId
      ),
      taxedAs: values.taxedAs || undefined,
      cStateId: values.cStateId || undefined,
      dateOfIncorporation: values.dateOfIncorporation || undefined,
      addressData,
      isPrimary: false,
      relationKind: mode === "separate" ? undefined : mode,
      relatedEntityId:
        mode === "separate" ? undefined : values.relatedEntityId || undefined,
    });
  };

  return (
    <FormikForm<AddEntityFormValues>
      initialValues={initialValues}
      enableReinitialize
      validationSchema={buildAddEntitySchema(
        mode,
        showBusinessAddress,
        showDateField,
        requireStateField,
        usCountryIds
      )}
      validateOnChange
      onSubmit={handleSubmit}
    >
      <AddEntityFormFields
        mode={mode}
        relationOptions={relationOptions}
        countries={countries}
        showBusinessAddress={showBusinessAddress}
        showStateField={showStateField}
        requireStateField={requireStateField}
        showDateField={showDateField}
        singleColumn={singleColumn}
        hiddenTaxOptionKeys={hiddenTaxOptionKeys}
        onCancel={onCancel}
      />
    </FormikForm>
  );
};
